import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["step", "indicator", "nextButton", "prevButton", "submitButton", "progress", "timeSlotsError"];

  connect() {
    this.currentStep = 0;
    const shouldScroll = this.isFormPartiallyFilled();
    this.showStep(this.currentStep, shouldScroll);
    this.detectAndJumpToErrors();
    this.setupTimeSlotsListeners();
  }

  setupTimeSlotsListeners() {
    const allTimeSlots = this.element.querySelectorAll("input[name='tutor_request[time_slots][]']");
    allTimeSlots.forEach((cb) => {
      cb.addEventListener("change", () => this.clearTimeSlotsErrors());
    });
  }

  clearTimeSlotsErrors() {
    this.timeSlotsErrorTargets.forEach((el) => el.classList.add("hidden"));
  }

  isFormPartiallyFilled() {
    // Check text, tel, and textarea inputs
    const textInputs = this.element.querySelectorAll("input[type='text'], input[type='tel'], textarea");
    for (const input of textInputs) {
      if (input.value && input.value.trim() !== "") {
        return true;
      }
    }

    // Check selects
    const selects = this.element.querySelectorAll("select");
    for (const select of selects) {
      if (select.value && select.value.trim() !== "") {
        return true;
      }
    }

    // Check checkboxes (like preferred time blocks)
    const checkboxes = this.element.querySelectorAll("input[type='checkbox']");
    for (const cb of checkboxes) {
      if (cb.checked) {
        return true;
      }
    }

    return false;
  }

  showStep(index, scroll = true) {
    this.stepTargets.forEach((el, i) => {
      el.classList.toggle("hidden", i !== index);
    });

    // Update indicators
    const activeClasses = ["bg-blue-600", "text-white", "border-blue-600", "dark:bg-blue-600", "dark:border-blue-600"];
    const completedClasses = ["bg-green-600", "text-white", "border-green-600", "dark:bg-green-600", "dark:border-green-600"];
    const inactiveClasses = ["bg-gray-200", "text-gray-600", "border-gray-200", "dark:bg-slate-800", "dark:text-slate-400", "dark:border-slate-800"];
    const allStates = [...activeClasses, ...completedClasses, ...inactiveClasses];

    this.indicatorTargets.forEach((el, i) => {
      el.classList.remove(...allStates);
      if (i < index) {
        el.classList.add(...completedClasses);
        el.innerHTML = "&#10003;"; // Checkmark symbol
      } else if (i === index) {
        el.classList.add(...activeClasses);
        el.textContent = i + 1;
      } else {
        el.classList.add(...inactiveClasses);
        el.textContent = i + 1;
      }
    });

    // Update button visibility
    if (this.hasPrevButtonTarget) this.prevButtonTarget.classList.toggle("hidden", index === 0);
    if (this.hasNextButtonTarget) this.nextButtonTarget.classList.toggle("hidden", index === this.stepTargets.length - 1);
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.toggle("hidden", index !== this.stepTargets.length - 1);

    // Scroll to the wizard progress indicator so the user has
    // context of which step they're on. Falls back to the form
    // wrapper if the progress target isn't present.
    if (scroll) {
      const target = this.hasProgressTarget ? this.progressTarget : this.element;
      target.scrollIntoView({behavior: "smooth", block: "start"});
    }
  }

  next(event) {
    event.preventDefault();
    if (this.validateStep(this.currentStep)) {
      this.currentStep++;
      this.showStep(this.currentStep);
    }
  }

  back(event) {
    event.preventDefault();
    if (this.currentStep > 0) {
      this.currentStep--;
      this.showStep(this.currentStep);
    }
  }

  validateStep(stepIndex) {
    const stepContainer = this.stepTargets[stepIndex];
    let valid = true;

    // 1. Text inputs, selects, radio buttons, and textareas with standard required validation
    const inputs = stepContainer.querySelectorAll("input[required]:not([type='checkbox']), select[required], textarea[required]");
    for (const input of inputs) {
      if (!input.checkValidity()) {
        input.reportValidity();
        return false;
      }
    }

    // 2. Time slots checkboxes (required on Step 2)
    // In Rails, time_slots is an array (e.g., tutor_request[time_slots][])
    const timeSlots = stepContainer.querySelectorAll("input[name='tutor_request[time_slots][]']");
    if (timeSlots.length > 0) {
      const anyChecked = Array.from(timeSlots).some((cb) => cb.checked);
      if (!anyChecked) {
        const firstCb = timeSlots[0];
        // Show the inline error (if the template provided one)
        this.timeSlotsErrorTargets.forEach((el) => el.classList.remove("hidden"));
        // Scroll the schedule section into view so the user actually sees it
        const scrollTarget = firstCb.closest(".block") || firstCb;
        scrollTarget.scrollIntoView({behavior: "smooth", block: "center"});
        // Move focus to the first checkbox for keyboard / screen-reader users
        firstCb.focus({preventScroll: true});
        return false;
      }
    }

    // 3. Minimum monthly fee (the fee box shows a red error state below the
    //    effective minimum: RM350 long-term, RM500 short-term).
    //    The gate fires only while the box is visible; `data-fee-below` is
    //    forced to "false" when the server does not enforce the minimum.
    const feeContainer = stepContainer.querySelector('[data-subtotal-target="container"]');
    if (feeContainer && !feeContainer.classList.contains("hidden") && feeContainer.dataset.feeBelow === "true") {
      feeContainer.scrollIntoView({behavior: "smooth", block: "center"});
      feeContainer.setAttribute("tabindex", "-1");
      feeContainer.focus({preventScroll: true});
      return false;
    }

    return true;
  }

  detectAndJumpToErrors() {
    // If the server returns validations, find which step contains a .field_with_errors element and navigate to it
    this.stepTargets.forEach((el, index) => {
      if (el.querySelector(".field_with_errors")) {
        this.currentStep = index;
        this.showStep(this.currentStep);
      }
    });
  }
}
